import APITemplate from '@api/APITemplate'
import { diffTimeMinutes } from '@utils/Format'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import store from '@state/store'
import con from '@services/connection'
import { openFinance } from '@modules/financial-planning/constants/URL'

class APIFinancialPlanningOpenFinance extends APITemplate {
  constructor() {
    super()
    this.reqConnectors = null
    this.reqClientConnectors = null
  }

  _shouldSkipConnectorsLoad(forceUpdate) {
    if (!forceUpdate) {
      const shouldUpdate = diffTimeMinutes(
        store.getters['financialPlanning/openFinanceConnectorsLastUpdate'],
        MINUTES_TIMEOUT_REQUESTS
      )

      if (!shouldUpdate) {
        return store.getters['financialPlanning/openFinanceConnectors']
      }
    }

    if (store.getters['financialPlanning/openFinanceConnectorsLoading']) {
      return store.getters['financialPlanning/openFinanceConnectors']
    }

    return null
  }

  _handleConnectorsResponse(response) {
    if (response && response.status === 200) {
      store.dispatch(
        'financialPlanning/setOpenFinanceConnectors',
        response.value || { value: [], meta: null }
      )
    } else {
      store.dispatch('financialPlanning/setOpenFinanceConnectors', {
        value: [],
        meta: null,
      })
    }
  }

  async _executeConnectorsRequest(requestUrl) {
    store.dispatch('financialPlanning/setOpenFinanceConnectorsLoading', true)
    this.reqConnectors = con.get(requestUrl)

    try {
      const response = await this.reqConnectors
      this._handleConnectorsResponse(response)
      return response
    } catch (error) {
      this._handleConnectorsResponse(null)
      throw error
    } finally {
      store.dispatch('financialPlanning/setOpenFinanceConnectorsLoading', false)
      this.reqConnectors = null
    }
  }

  async carregarConnectors({ params = {}, forceUpdate = false } = {}) {
    const skipResult = this._shouldSkipConnectorsLoad(forceUpdate)
    if (skipResult) return skipResult

    if (this.reqConnectors) {
      return this.reqConnectors
    }

    const response = await this._executeConnectorsRequest(openFinance.connectors())

    return response || {}
  }

  _shouldSkipClientConnectorsLoad(clientId, forceUpdate) {
    if (!clientId) {
      return { status: 400, message: 'Cliente não informado.' }
    }

    if (!forceUpdate) {
      const shouldUpdate = diffTimeMinutes(
        store.getters['financialPlanning/openFinanceClientConnectorsLastUpdate'],
        MINUTES_TIMEOUT_REQUESTS
      )

      if (!shouldUpdate) {
        return store.getters['financialPlanning/openFinanceClientConnectors']
      }
    }

    if (store.getters['financialPlanning/openFinanceClientConnectorsLoading']) {
      return store.getters['financialPlanning/openFinanceClientConnectors']
    }

    return null
  }

  _handleClientConnectorsResponse(response, clientId) {
    if (response && response.status === 200) {
      store.dispatch('financialPlanning/setOpenFinanceClientConnectors', {
        ...response,
        clientId,
      })
    } else {
      store.dispatch('financialPlanning/setOpenFinanceClientConnectors', {
        value: { value: [] },
        clientId: null,
      })
    }
  }

  async _executeClientConnectorsRequest(clientId, requestUrl) {
    store.dispatch(
      'financialPlanning/setOpenFinanceClientConnectorsLoading',
      true
    )
    this.reqClientConnectors = con.get(requestUrl)

    try {
      const response = await this.reqClientConnectors
      this._handleClientConnectorsResponse(response, clientId)
      return response
    } catch (error) {
      this._handleClientConnectorsResponse(null, clientId)
      throw error
    } finally {
      store.dispatch(
        'financialPlanning/setOpenFinanceClientConnectorsLoading',
        false
      )
      this.reqClientConnectors = null
    }
  }

  async carregarClientConnectors({
    clientId,
    params = {},
    forceUpdate = false,
  } = {}) {
    const skipResult = this._shouldSkipClientConnectorsLoad(clientId, forceUpdate)
    if (skipResult) return skipResult

    if (this.reqClientConnectors) {
      return this.reqClientConnectors
    }

    const requestUrl = openFinance.clientConnectors(clientId)
    const response = await this._executeClientConnectorsRequest(clientId, requestUrl)

    return response || {}
  }

  criarClientConnector({
    clientId,
    connectorId,
    credentials = {},
    sincronizaConsolidador = false,
  } = {}) {
    if (!clientId || !connectorId) {
      return {
        status: 400,
        message: 'Cliente ou conector não informado.',
      }
    }

    const requestUrl = openFinance.clientConnectors(clientId)

    return con.post(requestUrl, {
      id_conector: connectorId,
      credentials: JSON.stringify(credentials),
      sincroniza_consolidador: sincronizaConsolidador,
    })
  }

  sincronizarClientConnector({ clientId, connectorId } = {}) {
    if (!clientId || !connectorId) {
      return {
        status: 400,
        message: 'Cliente ou conector não informado.',
      }
    }

    const requestUrl = openFinance.connectorSynchronization(
      clientId,
      connectorId
    )

    return con.put(requestUrl, {})
  }

  removerClientConnector({ clientId, connectorId } = {}) {
    if (!clientId || !connectorId) {
      return {
        status: 400,
        message: 'Cliente ou conector não informado.',
      }
    }

    const requestUrl = `${openFinance.clientConnectors(
      clientId
    )}/${connectorId}`

    return con.delet(requestUrl)
  }

  atualizarClientConnector({
    clientId,
    connectorId,
    sincronizaConsolidador,
  } = {}) {
    if (!clientId || !connectorId) {
      return {
        status: 400,
        message: 'Cliente ou conector não informado.',
      }
    }

    const requestUrl = `${openFinance.clientConnectors(
      clientId
    )}/${connectorId}`

    return con.put(requestUrl, {
      data: JSON.stringify({
        sincroniza_consolidador: sincronizaConsolidador,
      }),
    })
  }
}

export const apiFinancialPlanningOpenFinance =
  new APIFinancialPlanningOpenFinance()

export default apiFinancialPlanningOpenFinance
